import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars, SettlementQuery } from '../../utils/settlement';
import OrderFills, { FillSummary } from './shared/OrderFills';
import {
  DerivativeMetricGrid,
  DerivativeMetricLabel,
  DerivativeMetricTile,
  DerivativeMetricValue,
  DerivativePreviewButton,
  DerivativePreviewToolbar,
  DerivativeReferenceHint
} from './shared/DerivativeTicketLayout';

import { DateField } from '../shared/DateField';

/**
 * Options trade entry — SMF/economics split (same pattern as the PC/LL and
 * structured tickets): the option CONTRACT is defined once in Reference Data
 * (Structured Products > Listed Options -> POST /api/derivatives/options/define)
 * and this ticket only references it and captures trade economics (direction,
 * contracts, premium, commission, dates) plus valuation-preview inputs.
 *
 * buildTransactionData calls /prepare with the picked contractId — the backend
 * short-circuits to the existing contract/security and returns a fresh
 * per-trade sourceRef, so the posting engine routes to OptionsPostingSpi with
 * no reference-field re-entry and no silent instrument minting from a trade.
 */

// ADR 0009 §4.2: listed options trade and settle on the NYSE calendar. The ticket shows the
// settle date the server will store; the browser's count stands in until the server answers.
const settleQuery = (tradeDate: string, currency?: string): SettlementQuery =>
  ({ tradeDate, ticketType: 'OPTIONS', convention: 'T+1', currency: currency || 'USD' });

/** Applies the server's settle date if the form still holds the inputs and the date it was asked about. */
const applyServerSettleDate = (setFormData: any, query: SettlementQuery, expected: string) => {
  resolveSettleDate(query).then(settleDate => setFormData((prev: any) =>
    prev.tradeDate === query.tradeDate && (prev.currency || 'USD') === query.currency
      && prev.settleDate === expected && settleDate && settleDate !== expected
      ? { ...prev, settleDate } : prev));
};

/** After a trade date change: the browser's count at once, then the server's. */
const refreshSettleDate = (setFormData: any, query: SettlementQuery) => {
  if (!query.tradeDate) return;
  const provisional = provisionalSettleDate(query);
  setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
  applyServerSettleDate(setFormData, query, provisional);
};

// F-HG-017: built per mount — a module-level literal froze trade/settle
// dates (and the operator) at first import for the whole browser session.
const makeInitialFormData = () => ({
  portfolio: '', assetManager: '', portfolioManager: '',
  // Instrument reference (locked; populated by picking a contract)
  contractSymbol: '', _existingContractId: '',
  contractName: '', underlyingSymbol: '', assetClass: '', exchange: '',
  optionType: '', exerciseStyle: '', settlementType: '',
  strikePrice: '', contractMultiplier: '', expiryDate: '', currency: 'USD',
  // Trade economics
  direction: 'LONG', contracts: '', tradePrice: '', commission: '',
  tradeDate: getTodayDate(calendarsFor('OPTIONS')),
  settleDate: provisionalSettleDate(settleQuery(getTodayDate(calendarsFor('OPTIONS')))),
  // Valuation-preview inputs (seeded from the contract's inception marks)
  underlyingPrice: '', impliedVol: '', riskFreeRate: '',
  submittedBy: getCurrentOperatorName(), executedBy: '', notes: '',
});

const numOrNull = (v: any) => (v === '' || v == null ? null : Number(v));

const DerivativesOptionsTicketScreen: React.FC = () => {
  const initialFormData = React.useMemo(makeInitialFormData, []);
  const validateForm = (formData: any, action: string) => {
    const errors: { [k: string]: string } = {};
    if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
    // A weekend or holiday typed into the trade date leaves it empty (ADR 0009): refuse the save.
    if (!formData.tradeDate) errors.tradeDate = 'Trade Date is required (a business day for this contract)';
    if (!formData._existingContractId) errors.contractSymbol = 'Select an option contract (or define one in Reference Data)';
    if (!formData.contracts || Number(formData.contracts) <= 0) errors.contracts = 'Contracts > 0 required';
    if (formData.tradePrice === '' || Number(formData.tradePrice) <= 0) errors.tradePrice = 'Premium > 0 required';
    if (action === 'execute' && !formData.executedBy?.trim()) errors.executedBy = 'Executed By is required for execution';
    if (action === 'execute' && !(Number(formData._filledQty) > 0)) errors.fills = 'Record at least one fill before executing';
    return errors;
  };

  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);
    if (!formData._existingContractId) throw new Error('Select an option contract before saving');
    const isShort = formData.direction === 'SHORT';

    // Contract already defined in Reference Data — prepare short-circuits by
    // contractId and returns the minted security + a fresh per-trade sourceRef.
    const prepareRes = await axios.post('/api/derivatives/options/prepare', {
      contractId: Number(formData._existingContractId),
      direction: formData.direction,
      contracts: numOrNull(formData.contracts),
      tradePrice: numOrNull(formData.tradePrice),
      tradeDate: formData.tradeDate,
      currency: formData.currency || 'USD',
    });
    const { securityId, sourceRef } = prepareRes.data;

    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager?.trim()) {
      const pm = dimensionData.portfolioManagers.find((x: any) => x.portfolioManagerName === formData.portfolioManager.trim());
      if (pm?.id) portfolioManagerId = pm.id;
    }
    let assetManagerId: number | null = null;
    if (formData.assetManager?.trim()) {
      const am = dimensionData.assetManagers.find((x: any) => x.assetManagerName === formData.assetManager.trim());
      if (am?.id) assetManagerId = am.id;
    }

    const contracts = Number(formData.contracts);
    const premium = Number(formData.tradePrice);
    const commission = formData.commission ? Number(formData.commission) : 0;
    // amount = premium notional (price × multiplier × contracts), so the blotter
    // "Amount" reads as the $ premium — not the bare contract count.
    const premiumNotional = premium * Number(formData.contractMultiplier || 100) * contracts;
    // netAmount = the trade's full cash consideration (ties to booked cash):
    // LONG pays premium + commission; SHORT receives premium net of commission.
    const netCash = isShort ? premiumNotional - commission : premiumNotional + commission;
    return {
      transactionType: isShort ? 'SELL' : 'BUY',
      securityId, portfolioId: portfolio.id, assetManagerId, portfolioManagerId,
      quantity: contracts, price: premium, amount: premiumNotional, netAmount: netCash, commission,
      tradeDate: formData.tradeDate, settleDate: formData.settleDate, currency: formData.currency || 'USD',
      status: 'DRAFT', ticketType: 'OPTIONS', eventType: 'ACQUIRE', sourceRef,
      createdBy: formData.submittedBy, executedBy: formData.executedBy || null,
      notes: formData.notes || `Option ${formData.direction} ${contracts} ${formData.contractSymbol} ${formData.optionType} strike ${formData.strikePrice} @ ${premium}`,
    };
  };

  const loadTransactionData = async (t: any) => {
    const fresh = makeInitialFormData();
    const base = {
      ...fresh,
      portfolio: t.portfolioName || '', assetManager: t.assetManagerName || '', portfolioManager: t.portfolioManagerName || '',
      submittedBy: t.createdBy || getCurrentOperatorName(),
      executedBy: t.executedBy || '', notes: t.notes || '',
      tradeDate: t.tradeDate || fresh.tradeDate,
      settleDate: t.settleDate || provisionalSettleDate(settleQuery(t.tradeDate || fresh.tradeDate, t.currency)),
      currency: t.currency || 'USD',
      direction: t.transactionType === 'SELL' ? 'SHORT' : 'LONG',
      contracts: t.quantity != null ? String(Math.abs(Number(t.quantity))) : '',
      tradePrice: t.price != null ? String(t.price) : '',
      commission: t.commission != null ? String(t.commission) : '',
    };
    const m = (t.sourceRef || '').match(/option-contract:(\d+)/);
    if (!m) return base;
    try {
      const c = (await axios.get(`/api/derivatives/options/contract/${m[1]}`)).data;
      return {
        ...base,
        _existingContractId: String(c.contractId ?? m[1]),
        contractSymbol: c.contractSymbol || '', contractName: c.contractName || '',
        underlyingSymbol: c.underlyingSymbol || '', assetClass: c.assetClass || '',
        exchange: c.exchange || '', optionType: c.optionType || '',
        exerciseStyle: c.exerciseStyle || '', settlementType: c.settlementType || '',
        strikePrice: c.strikePrice != null ? String(c.strikePrice) : '',
        contractMultiplier: c.contractMultiplier != null ? String(c.contractMultiplier) : '100',
        expiryDate: c.expiryDate ? String(c.expiryDate).slice(0, 10) : '',
        underlyingPrice: c.underlyingPrice != null ? String(c.underlyingPrice) : '',
        impliedVol: c.impliedVol != null ? String(c.impliedVol) : '',
        riskFreeRate: c.riskFreeRate != null ? String(c.riskFreeRate) : '',
      };
    } catch { return base; }
  };

  return (
    <BaseTicketScreen
      title="Options Trade Entry" assetClass="Derivatives" assetSubClass="Options"
      initialFormData={initialFormData} validateForm={validateForm} buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, handleInputChange, isFieldDisabled, dimensionData, transactionStatus, editingTransactionId }) => (
        <OptionsFields formData={formData} setFormData={setFormData} errors={errors} handleInputChange={handleInputChange}
          isFieldDisabled={isFieldDisabled} dimensionData={dimensionData} transactionStatus={transactionStatus}
          editingTransactionId={editingTransactionId} />
      )}
    </BaseTicketScreen>
  );
};

const OptionsFields: React.FC<{ formData: any; setFormData: any; errors: any; handleInputChange: (f: string, v: string) => void; isFieldDisabled: (f: string) => boolean; dimensionData: any; transactionStatus: any; editingTransactionId: number | null; }>
  = ({ formData, setFormData, errors, handleInputChange, isFieldDisabled, dimensionData, transactionStatus, editingTransactionId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [preview, setPreview] = useState<any>(null);
  const [calcBusy, setCalcBusy] = useState(false);
  const [sugg, setSugg] = useState<any[]>([]);
  const [showSugg, setShowSugg] = useState(false);
  const dis = (f: string) => isFieldDisabled(f);

  // A new ticket asks the server for its settle date on open; a reopened trade keeps its own.
  useEffect(() => {
    const state: any = location.state;
    if (editingTransactionId || state?.transactionId || state?.id || !formData.tradeDate) return;
    applyServerSettleDate(setFormData, settleQuery(formData.tradeDate, formData.currency), formData.settleDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const picked = !!formData._existingContractId;

  const onSymbolChange = async (raw: string) => {
    const v = raw.toUpperCase();
    handleInputChange('contractSymbol', v);
    if (formData._existingContractId) handleInputChange('_existingContractId', '');
    if (!v.trim()) { setSugg([]); setShowSugg(false); return; }
    try {
      const res = await axios.get(`/api/derivatives/options/contracts?q=${encodeURIComponent(v.trim())}`);
      setSugg(res.data || []); setShowSugg((res.data || []).length > 0);
    } catch { setSugg([]); setShowSugg(false); }
  };

  const pickContract = (c: any) => {
    handleInputChange('contractSymbol', c.contractSymbol || '');
    handleInputChange('contractName', c.contractName || '');
    handleInputChange('underlyingSymbol', c.underlyingSymbol || '');
    handleInputChange('assetClass', c.assetClass || '');
    handleInputChange('exchange', c.exchange || '');
    handleInputChange('optionType', c.optionType || '');
    handleInputChange('exerciseStyle', c.exerciseStyle || '');
    handleInputChange('settlementType', c.settlementType || '');
    handleInputChange('strikePrice', c.strikePrice != null ? String(c.strikePrice) : '');
    handleInputChange('contractMultiplier', c.contractMultiplier != null ? String(c.contractMultiplier) : '100');
    handleInputChange('expiryDate', c.expiryDate ? String(c.expiryDate).slice(0, 10) : '');
    if (c.notionalCurrency || c.currency) handleInputChange('currency', c.notionalCurrency || c.currency);
    if (c.underlyingPrice != null) handleInputChange('underlyingPrice', String(c.underlyingPrice));
    if (c.impliedVol != null) handleInputChange('impliedVol', String(c.impliedVol));
    if (c.riskFreeRate != null) handleInputChange('riskFreeRate', String(c.riskFreeRate));
    handleInputChange('_existingContractId', String(c.id ?? c.contractId));
    setShowSugg(false);
  };

  // F-HG-003: a hedge proposal used to arrive here and be silently discarded —
  // the ticket never read location.state, so identifier/notional/portfolio
  // vanished and the proposal was unbookable. When the proposal carries a
  // bookable contractId, auto-pick the contract and prefill the economics.
  const hedgeApplied = useRef(false);
  useEffect(() => {
    const hd: any = (location.state as any)?.hedgeData;
    const portfolioName: string | undefined = (location.state as any)?.portfolioName;
    if (hedgeApplied.current || !hd) return;
    hedgeApplied.current = true;
    if (portfolioName && !formData.portfolio) handleInputChange('portfolio', portfolioName);
    const provenance = `Hedge proposal${hd.identifier ? ` ${hd.identifier}` : ''}${hd.rationale ? `: ${hd.rationale}` : ''}${hd.notionalAmount ? ` — target notional $${Number(hd.notionalAmount).toLocaleString()}` : ''}`;
    if (!formData.notes) handleInputChange('notes', provenance);
    if (!hd.contractId || formData._existingContractId) return;
    (async () => {
      try {
        const c = (await axios.get(`/api/derivatives/options/contract/${hd.contractId}`)).data;
        pickContract({ ...c, id: c.contractId ?? hd.contractId });
        const mult = Number(c.contractMultiplier || 100);
        const uPx = Number(c.underlyingPrice || 0);
        const notional = Number(hd.notionalAmount || 0);
        if (notional > 0 && uPx > 0) {
          handleInputChange('contracts', String(Math.max(1, Math.round(notional / (mult * uPx)))));
        }
      } catch {
        // Contract lookup failed — leave the picker empty; the hint below
        // routes to Reference Data to define one.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCalculate = async () => {
    try {
      setCalcBusy(true);
      const isShort = formData.direction === 'SHORT';
      const res = await axios.post('/api/derivatives/options/calculate', {
        optionType: formData.optionType || 'CALL', direction: formData.direction,
        strikePrice: numOrNull(formData.strikePrice), contractMultiplier: numOrNull(formData.contractMultiplier),
        contracts: numOrNull(formData.contracts), tradePrice: numOrNull(formData.tradePrice),
        underlyingPrice: numOrNull(formData.underlyingPrice), impliedVol: numOrNull(formData.impliedVol),
        riskFreeRate: numOrNull(formData.riskFreeRate), expiryDate: formData.expiryDate || null,
        assetClass: formData.assetClass || 'EQUITY', tradeDate: formData.tradeDate,
      });
      setPreview({ ...res.data, isShort });
    } catch { setPreview(null); } finally { setCalcBusy(false); }
  };

  const fmt = (v: any) => (v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 }));
  const g = preview?.greeks || {};

  const refCell = (label: string, value: any) => (
    <div>
      <div style={{ color: '#64748b', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600, marginTop: 2 }}>{value || '—'}</div>
    </div>
  );

  return (
    <>
      {/* Row 1 — managers + execution */}
      <FormRow>
        <FormGroup>
          <Label>Asset Manager*</Label>
          <Select value={formData.assetManager || ''} disabled={dis('assetManager')} onChange={e => handleInputChange('assetManager', e.target.value)} className={errors.assetManager ? 'error' : ''}>
            <option value="">Select Asset Manager</option>
            {(dimensionData?.assetManagers || []).map((am: any) => <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>)}
          </Select>
          {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
        </FormGroup>
        <FormGroup>
          <Label>Portfolio Manager*</Label>
          <Select value={formData.portfolioManager || ''} disabled={dis('portfolioManager')} onChange={e => handleInputChange('portfolioManager', e.target.value)} className={errors.portfolioManager ? 'error' : ''}>
            <option value="">Select Portfolio Manager</option>
            {(dimensionData?.portfolioManagers || []).map((pm: any) => <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>)}
          </Select>
          {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
        </FormGroup>
        <FormGroup>
          <Label>Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
          <Select value={formData.executedBy || ''} disabled={dis('executedBy')} onChange={e => handleInputChange('executedBy', e.target.value)} className={errors.executedBy ? 'error' : ''}>
            <option value="">Select</option>
            {(dimensionData?.users || []).map((u: any) => <option key={u.id} value={u.fullName}>{u.fullName}{u.role ? ` (${u.role})` : ''}</option>)}
          </Select>
          {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* Row 2 — instrument reference (SMF): search-and-pick only */}
      <FormRow>
        <FormGroup style={{ gridColumn: '1 / -1' }}>
          <Label>Option Contract* (defined in Reference Data)</Label>
          <div style={{ position: 'relative' }}>
            <Input placeholder="Search by contract symbol or underlying (e.g. SPX)..." value={formData.contractSymbol || ''} disabled={dis('contractSymbol')}
              autoComplete="off"
              onChange={e => onSymbolChange(e.target.value)}
              onFocus={() => { if (sugg.length) setShowSugg(true); }}
              onBlur={() => setTimeout(() => setShowSugg(false), 150)}
              className={errors.contractSymbol ? 'error' : ''} />
            {showSugg && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4,
                background: '#0f172a', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)', maxHeight: 220, overflowY: 'auto' }}>
                {sugg.map((c: any) => (
                  <div key={c.id ?? c.contractId} onMouseDown={() => pickContract(c)}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(59,130,246,0.12)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{c.underlyingSymbol} {c.optionType} {c.strikePrice}
                      <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 8 }}>{c.exchange}</span></div>
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>{c.contractSymbol}
                      {c.expiryDate ? ` · exp ${String(c.expiryDate).slice(0, 10)}` : ''}
                      {c.contractMultiplier != null ? ` · ×${c.contractMultiplier}` : ''}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.contractSymbol && <FieldError>{errors.contractSymbol}</FieldError>}
          {!picked && (
            <DerivativeReferenceHint $tone="options">
              No matching contract?{' '}
              <span style={{ color: '#60a5fa', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/reference-data?tab=derivatives&new=1')}>
                Define new option contract →
              </span>{' '}
              (Reference Data · Derivatives · Listed Options)
            </DerivativeReferenceHint>
          )}
        </FormGroup>
      </FormRow>

      {/* Selected-contract reference card (read-only SMF attributes) */}
      {picked && (
        <div style={{ border: '1px solid rgba(148,163,184,0.18)', background: 'rgba(15,23,42,0.55)',
          borderRadius: 10, padding: '12px 16px', marginBottom: 16,
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
          {refCell('Underlying', `${formData.underlyingSymbol}${formData.assetClass ? ` (${formData.assetClass})` : ''}`)}
          {refCell('Type', formData.optionType)}
          {refCell('Strike', formData.strikePrice)}
          {refCell('Expiry', formData.expiryDate)}
          {refCell('Multiplier', formData.contractMultiplier ? `×${formData.contractMultiplier}` : '')}
          {refCell('Style', formData.exerciseStyle)}
          {refCell('Settlement', formData.settlementType)}
          {refCell('Exchange', formData.exchange)}
          {refCell('Currency', formData.currency)}
        </div>
      )}

      <DerivativeReferenceHint $tone="options">
        Post-trade lifecycle — mark, close, exercise, expiry — lives in{' '}
        <span style={{ color: '#60a5fa', cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => navigate('/middle-office?tab=derivatives-lifecycle')}>
          Middle Office · Derivatives Lifecycle →
        </span>
      </DerivativeReferenceHint>

      {/* Row 3 — trade economics */}
      <FormRow>
        <FormGroup>
          <Label>Direction*</Label>
          <Select value={formData.direction} disabled={dis('direction')} onChange={e => handleInputChange('direction', e.target.value)}>
            <option value="LONG">Long (pay premium)</option><option value="SHORT">Short (receive premium)</option>
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Contracts*</Label>
          <Input type="number" placeholder="10" value={formData.contracts || ''} disabled={dis('contracts')}
            onChange={e => handleInputChange('contracts', e.target.value)} className={errors.contracts ? 'error' : ''} />
          {errors.contracts && <FieldError>{errors.contracts}</FieldError>}
        </FormGroup>
        <FormGroup>
          <Label>Premium* (per share)</Label>
          <Input type="number" placeholder="12.50" value={formData.tradePrice || ''} disabled={dis('tradePrice')}
            onChange={e => handleInputChange('tradePrice', e.target.value)} className={errors.tradePrice ? 'error' : ''} />
          {errors.tradePrice && <FieldError>{errors.tradePrice}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* Row 4 — dates + commission (dates were previously invisible defaults:
          trade=today, settle=T+1 — operator E2E finding 2026-08-04) */}
      <FormRow>
        <FormGroup>
          <Label>Trade Date*</Label>
          <DateField
            value={formData.tradeDate || ''}
            disabled={dis('tradeDate')}
            calendars={calendarsFor('OPTIONS', formData.currency)}
            onChange={iso => {
              handleInputChange('tradeDate', iso);
              refreshSettleDate(setFormData, settleQuery(iso, formData.currency));
            }}
          />
        </FormGroup>
        <FormGroup>
          <Label>Settle Date*</Label>
          <DateField
            value={formData.settleDate || ''}
            disabled={dis('settleDate')}
            calendars={settlementCalendars({ ticketType: 'OPTIONS', currency: formData.currency })}
            onChange={iso => handleInputChange('settleDate', iso)}
          />
        </FormGroup>
        <FormGroup>
          <Label>Commission</Label>
          <Input type="number" placeholder="15" value={formData.commission || ''} disabled={dis('commission')}
            onChange={e => handleInputChange('commission', e.target.value)} />
        </FormGroup>
      </FormRow>

      {/* Row 5 — valuation-preview inputs */}
      <FormRow>
        <FormGroup>
          <Label>Underlying Price (preview)</Label>
          <Input type="number" placeholder="205" value={formData.underlyingPrice || ''} disabled={dis('underlyingPrice')}
            onChange={e => handleInputChange('underlyingPrice', e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>Implied Vol / Rate (decimals)</Label>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input type="number" placeholder="0.25" value={formData.impliedVol || ''} disabled={dis('impliedVol')}
              onChange={e => handleInputChange('impliedVol', e.target.value)} />
            <Input type="number" placeholder="0.045" value={formData.riskFreeRate || ''} disabled={dis('riskFreeRate')}
              onChange={e => handleInputChange('riskFreeRate', e.target.value)} />
          </div>
        </FormGroup>
      </FormRow>

      <DerivativePreviewToolbar>
        <DerivativePreviewButton type="button" onClick={handleCalculate} disabled={calcBusy || !picked} $tone="options">
          {calcBusy ? 'Calculating...' : 'Preview Greeks & Premium'}
        </DerivativePreviewButton>
      </DerivativePreviewToolbar>
      {preview && (
        <DerivativeMetricGrid>
          {preview.theoreticalPrice != null && <Cell label="Theoretical (BS)" value={`$${fmt(preview.theoreticalPrice)}`} />}
          <Cell label={preview.isShort ? 'Premium Received' : 'Premium Cost'} value={`$${fmt(preview.premiumCost)}`} />
          <Cell label="Market Value" value={`$${fmt(preview.marketValue)}`} />
          {preview.breakeven != null && <Cell label="Breakeven" value={`$${fmt(preview.breakeven)}`} />}
          {preview.deltaAdjustedExposure != null && <Cell label="Δ-Adj Exposure" value={`$${fmt(preview.deltaAdjustedExposure)}`} />}
          {g.delta != null && <Cell label="Delta" value={fmt(g.delta)} />}
          {g.gamma != null && <Cell label="Gamma" value={fmt(g.gamma)} />}
          {g.vega != null && <Cell label="Vega" value={fmt(g.vega)} />}
          {g.theta != null && <Cell label="Theta" value={fmt(g.theta)} />}
          {preview.shortOptionMargin != null && <Cell label="Short Margin" value={`$${fmt(preview.shortOptionMargin)}`} />}
        </DerivativeMetricGrid>
      )}

      <OrderFills tradeId={editingTransactionId} status={transactionStatus} orderPrice={formData.tradePrice}
        onSummary={(s: FillSummary) => {
          const v = String(s.filledQuantity);
          if (v !== (formData._filledQty ?? '')) handleInputChange('_filledQty', v);
        }} />
    </>
  );
};

const Cell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <DerivativeMetricTile $tone="options">
    <DerivativeMetricLabel>{label}</DerivativeMetricLabel>
    <DerivativeMetricValue $tone="options">{value}</DerivativeMetricValue>
  </DerivativeMetricTile>
);

export default DerivativesOptionsTicketScreen;
